import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useEffect, useId, useState } from "react";
import { Check, LogOut, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAccountant } from "@/lib/auth";
import { phonePlaceholder } from "@/lib/country";
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  API_URL,
  queryClient,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryError } from "@/components/shared/query-error";

interface Category {
  id: string;
  name: string;
}

export function Settings() {
  const { data: accountant } = useAccountant();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const nameId = useId();
  const businessNameId = useId();
  const phoneId = useId();
  const emailId = useId();

  useEffect(() => {
    if (accountant) {
      setName(accountant.name ?? "");
      setBusinessName(accountant.business_name ?? "");
      setPhone(accountant.phone ?? "");
    }
  }, [accountant]);

  const {
    data: categories,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    error: categoriesError,
  } = useQuery({
    queryKey: ["accountant-categories"],
    queryFn: () => apiGet<Category[]>("/api/accountant/categories"),
    enabled: !!accountant,
  });

  const [newCategory, setNewCategory] = useState("");

  const saveProfileMutation = useMutation({
    mutationFn: (data: Record<string, string>) =>
      apiPatch("/api/accountant/me", data),
    onSuccess: () => {
      toast.success("Profile saved");
      queryClient.invalidateQueries({ queryKey: ["accountant-me"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not save"),
  });

  const addCategoryMutation = useMutation({
    mutationFn: (name: string) =>
      apiPost<Category[]>("/api/accountant/categories", { name }),
    onSuccess: () => {
      setNewCategory("");
      toast.success("Category added");
      queryClient.invalidateQueries({ queryKey: ["accountant-categories"] });
      queryClient.invalidateQueries({ queryKey: ["accountant-me"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not add"),
  });

  const removeCategoryMutation = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/api/accountant/categories/${id}`),
    onSuccess: () => {
      toast.success("Category removed");
      queryClient.invalidateQueries({ queryKey: ["accountant-categories"] });
      queryClient.invalidateQueries({ queryKey: ["accountant-me"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not remove"),
  });

  const handleSave = () =>
    saveProfileMutation.mutate({
      name,
      business_name: businessName,
      phone,
    });

  const handleSignOut = async () => {
    try {
      await fetch(`${API_URL}/api/accountant/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } finally {
      queryClient.clear();
      toast.success("Signed out");
      navigate("/login", { replace: true });
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-xl font-semibold">Settings</h1>

      {/* Practice details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Practice details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={nameId}>Your name</Label>
            <Input
              id={nameId}
              value={name}
              onChange={(e) =>
                setName((e.target as HTMLInputElement).value)
              }
              placeholder="Jane Smith"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={businessNameId}>Business name</Label>
            <Input
              id={businessNameId}
              value={businessName}
              onChange={(e) =>
                setBusinessName((e.target as HTMLInputElement).value)
              }
              placeholder="Smith & Co Accounting"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={phoneId}>Phone</Label>
              <Input
                id={phoneId}
                value={phone}
                onChange={(e) => setPhone((e.target as HTMLInputElement).value)}
                placeholder={phonePlaceholder(accountant?.country_config)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={emailId}>Email</Label>
              <Input
                id={emailId}
                value={accountant?.email ?? ""}
                readOnly
                className="bg-muted text-foreground"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expense categories */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Expense categories</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 rounded-md border border-info/20 bg-info-muted/60 p-3 text-xs text-foreground/80">
            <p className="font-medium text-foreground">
              Why your category list matters
            </p>
            <p>
              Taxr's AI reads each client receipt and picks the best match
              from the categories below. The more specific your list, the
              better the automatic classification, and the less manual
              cleanup you or your clients end up doing.
            </p>
            <p>
              When a client accepts your invite, their category list is
              replaced with this one. Any categories they've already used on
              confirmed receipts are preserved; the rest are removed. Unused
              client-added categories are cleaned up too.
            </p>
          </div>
          {isCategoriesLoading ? (
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-7 w-24 rounded-full" />
              ))}
            </div>
          ) : isCategoriesError ? (
            <QueryError
              error={categoriesError}
              message="Could not load categories"
              onRetry={() => queryClient.invalidateQueries({ queryKey: ["accountant-categories"] })}
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              {(categories ?? []).map((cat) => (
                <span
                  key={cat.id}
                  className="inline-flex items-center gap-1.5 rounded-full bg-muted pl-3 pr-1 py-1 text-xs font-medium capitalize"
                >
                  {cat.name.replace(/_/g, " ")}
                  <button
                    type="button"
                    onClick={() => removeCategoryMutation.mutate(cat.id)}
                    className="flex size-5 items-center justify-center rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                    aria-label={`Remove ${cat.name}`}
                    disabled={removeCategoryMutation.isPending}
                  >
                    <Trash2 className="size-3" />
                  </button>
                </span>
              ))}
              {(!categories || categories.length === 0) && (
                <p className="text-xs text-muted-foreground">No categories yet.</p>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Input
              value={newCategory}
              onChange={(e) =>
                setNewCategory((e.target as HTMLInputElement).value)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && newCategory.trim()) {
                  addCategoryMutation.mutate(newCategory.trim());
                }
              }}
              placeholder="Add a new category"
              className="max-w-xs"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => addCategoryMutation.mutate(newCategory.trim())}
              disabled={!newCategory.trim() || addCategoryMutation.isPending}
            >
              <Plus className="mr-1 size-3" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Security</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Sign out</Label>
            <p className="text-xs text-muted-foreground">
              Ends your current session on this device. You'll need a new magic
              link to sign back in.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSignOut}
              className="text-destructive"
            >
              <LogOut className="mr-1.5 size-4" />
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saveProfileMutation.isPending}
        >
          {saveProfileMutation.isSuccess ? (
            <Check className="mr-1.5 size-4" />
          ) : (
            <Save className="mr-1.5 size-4" />
          )}
          {saveProfileMutation.isPending ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
